// Settings → Prioritization, Quick-reply rules (admin only, 2026-10-29,
// docs/decisions/2026-10-29-quick-reply-priority-rule.md). Regardless of
// intent, when a customer answers our message within N minutes, the contact
// moves up to level L in the Queue's Oldest First order while that message
// is unanswered. Stored in quick_reply_priority_rules (migration 0167), keyed
// by company, channel and an optional TRQ client code. A change asks for a
// typed confirmation like the intent levels; each change is recorded and
// "View rule history" lists them, newest first.
import { useEffect, useState, useCallback } from 'react'
import {
  Box, Stack, Typography, Button, TextField, Paper, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Link, Switch, FormControlLabel,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material'
import { fetchQuickReplyRules, createQuickReplyRule, updateQuickReplyRule, fetchQuickReplyRuleHistory, INTENT_PRIORITIES_CHANGED_EVENT } from '../../api.js'
import { PRIORITY_LEVELS, quickReplyRuleKeyLabel, quickReplyChangeMessage, validQuickReplyMinutes, quickReplyHistoryChange } from '../../prioritization.js'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) + ' ET'
}

// What the admin types to confirm a change: the rule's key, e.g. "1A ebay".
const confirmTextOf = (rule) => [rule.company, rule.channel, rule.trqClientCode].filter(Boolean).join(' ')

function RuleChangeConfirmDialog({ pending, saving, error, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('')
  useEffect(() => { if (!pending) setTyped('') }, [pending])
  const expected = pending ? confirmTextOf(pending.rule) : ''
  return (
    <Dialog open={Boolean(pending)} onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>Change quick-reply rule</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography>{pending ? quickReplyChangeMessage(pending.rule, pending.changes) : ''}</Typography>
        <TextField
          size="small" fullWidth sx={{ mt: 2 }} autoFocus
          label="Type the rule to confirm"
          placeholder={expected}
          helperText={`Type "${expected}" exactly to enable the confirm button.`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm} disabled={saving || typed !== expected}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
          {saving ? 'Saving…' : 'Change rule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function AddRuleDialog({ open, options, onClose, onCreated }) {
  const blank = { company: '', channel: '', trqClientCode: '', windowMinutes: '60', level: 1, enabled: true }
  const [form, setForm] = useState(blank)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => { if (open) { setForm(blank); setError(null) } }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const valid = form.company && form.channel && validQuickReplyMinutes(form.windowMinutes)
  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await createQuickReplyRule({
        company: form.company, channel: form.channel, trqClientCode: form.trqClientCode || null,
        enabled: form.enabled, windowMinutes: Number(form.windowMinutes), level: Number(form.level),
      })
      onCreated(res.rule)
    } catch (e) {
      setError(e.status === 409 ? 'A rule for this company, channel and client already exists. Edit that one instead.' : e.message)
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Add quick-reply rule</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="qr-add-company">Company</InputLabel>
            <Select labelId="qr-add-company" label="Company" value={form.company} onChange={set('company')} inputProps={{ 'aria-label': 'Company' }}>
              {(options?.companies ?? []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="qr-add-channel">Channel</InputLabel>
            <Select labelId="qr-add-channel" label="Channel" value={form.channel} onChange={set('channel')} inputProps={{ 'aria-label': 'Channel' }}>
              {(options?.channels ?? []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="qr-add-client" shrink>Client</InputLabel>
            <Select labelId="qr-add-client" label="Client" value={form.trqClientCode} onChange={set('trqClientCode')} displayEmpty notched inputProps={{ 'aria-label': 'Client' }}>
              <MenuItem value="">Any client</MenuItem>
              {(options?.clientCodes ?? []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Minutes" type="number" value={form.windowMinutes} onChange={set('windowMinutes')}
            error={!validQuickReplyMinutes(form.windowMinutes)} helperText="A whole number of minutes, 1 or more."
            inputProps={{ min: 1, step: 1, 'aria-label': 'Minutes' }} />
          <FormControl size="small" fullWidth>
            <InputLabel id="qr-add-level">Level</InputLabel>
            <Select labelId="qr-add-level" label="Level" value={form.level} onChange={set('level')} inputProps={{ 'aria-label': 'Level' }}>
              {PRIORITY_LEVELS.map((l) => <MenuItem key={l} value={l}>{`Level ${l}`}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />} label="Enabled" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !valid}>{saving ? 'Saving…' : 'Add rule'}</Button>
      </DialogActions>
    </Dialog>
  )
}

function RuleHistoryDialog({ open, onClose }) {
  const [history, setHistory] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (!open) return
    setHistory(null)
    setError(null)
    fetchQuickReplyRuleHistory().then(setHistory).catch((e) => setError(e.message))
  }, [open])
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Quick-reply rule history</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {history === null && !error ? (
          <CircularProgress size={18} />
        ) : history?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No changes recorded yet.</Typography>
        ) : history ? (
          <Table size="small" data-testid="quick-reply-rule-history">
            <TableHead>
              <TableRow>
                <TableCell>Rule</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Date / time</TableCell>
                <TableCell>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{quickReplyRuleKeyLabel(h)}</TableCell>
                  <TableCell>{quickReplyHistoryChange(h)}</TableCell>
                  <TableCell>{formatDate(h.changedAt)}</TableCell>
                  <TableCell>{h.changedBy ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  )
}

// The minutes cell: edit the number, then Save opens the confirmation.
function MinutesCell({ rule, onPropose }) {
  const [draft, setDraft] = useState(String(rule.windowMinutes))
  useEffect(() => { setDraft(String(rule.windowMinutes)) }, [rule.windowMinutes])
  const dirty = draft !== String(rule.windowMinutes)
  const valid = validQuickReplyMinutes(draft)
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField size="small" type="number" value={draft} onChange={(e) => setDraft(e.target.value)} error={!valid}
        sx={{ width: 100 }} inputProps={{ min: 1, step: 1, 'aria-label': `Minutes for ${quickReplyRuleKeyLabel(rule)}` }} />
      {dirty && (
        <Button size="small" disabled={!valid} onClick={() => onPropose(rule, { windowMinutes: Number(draft) })}>Save</Button>
      )}
    </Stack>
  )
}

export default function QuickReplyRulesSection() {
  const [data, setData] = useState(null) // { rules, options }
  const [loadError, setLoadError] = useState(null)
  const [pending, setPending] = useState(null) // { rule, changes }
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)

  const load = useCallback(() => {
    fetchQuickReplyRules().then(setData).catch((e) => setLoadError(e.message))
  }, [])
  useEffect(() => { load() }, [load])

  const replaceRule = (rule) => setData((d) => ({ ...d, rules: d.rules.map((r) => (r.id === rule.id ? rule : r)) }))

  const confirmChange = async () => {
    if (!pending) return
    setSaving(true)
    setSaveError(null)
    try {
      const res = await updateQuickReplyRule(pending.rule.id, pending.changes)
      replaceRule(res.rule)
      // This tab's session tick refreshes now, so its Queue reloads at once;
      // other tabs see the new priorities version on their next tick.
      window.dispatchEvent(new Event(INTENT_PRIORITIES_CHANGED_EVENT))
      setPending(null)
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ mt: 4 }} data-testid="quick-reply-rules">
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h6">Quick-reply rules</Typography>
        <Link component="button" variant="body2" onClick={() => setHistoryOpen(true)}>View rule history</Link>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" onClick={() => setAddOpen(true)} disabled={!data}>Add rule</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Whatever the intent, when a customer answers our message within the minutes below, the contact moves up to the rule's level (or stays at its intent level if that is already higher) until we reply. It only changes the Queue's Oldest First order. When several rules match a contact, the best level among those whose minutes hold wins; a disabled rule does nothing.
      </Typography>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {data === null && !loadError ? (
        <CircularProgress size={20} />
      ) : data ? (
        <Paper variant="outlined">
          <Table size="small" data-testid="quick-reply-rules-table">
            <TableHead>
              <TableRow>
                <TableCell>Rule (company · channel · client)</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Minutes</TableCell>
                <TableCell sx={{ width: 140 }}>Level</TableCell>
                <TableCell>Last changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rules.map((r) => (
                <TableRow key={r.id} data-testid={`quick-reply-rule-${confirmTextOf(r).replace(/\s+/g, '-')}`}>
                  <TableCell>{quickReplyRuleKeyLabel(r)}</TableCell>
                  <TableCell>
                    <Switch size="small" checked={r.enabled} onChange={(e) => setPending({ rule: r, changes: { enabled: e.target.checked } })}
                      inputProps={{ 'aria-label': `Enabled for ${quickReplyRuleKeyLabel(r)}` }} />
                  </TableCell>
                  <TableCell><MinutesCell rule={r} onPropose={(rule, changes) => setPending({ rule, changes })} /></TableCell>
                  <TableCell>
                    <Select size="small" value={r.level}
                      onChange={(e) => { const level = Number(e.target.value); if (level !== r.level) setPending({ rule: r, changes: { level } }) }}
                      inputProps={{ 'aria-label': `Level for ${quickReplyRuleKeyLabel(r)}` }}>
                      {PRIORITY_LEVELS.map((l) => <MenuItem key={l} value={l}>{`Level ${l}`}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{r.updatedBy ?? '—'}{r.updatedAt ? ` · ${formatDate(r.updatedAt)}` : ''}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {data.rules.length === 0 && (
                <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No quick-reply rules.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      <RuleChangeConfirmDialog pending={pending} saving={saving} error={saveError} onConfirm={confirmChange}
        onCancel={() => { setPending(null); setSaveError(null) }} />
      <AddRuleDialog open={addOpen} options={data?.options} onClose={() => setAddOpen(false)}
        onCreated={(rule) => { setData((d) => ({ ...d, rules: [...d.rules, rule] })); setAddOpen(false); window.dispatchEvent(new Event(INTENT_PRIORITIES_CHANGED_EVENT)) }} />
      <RuleHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </Box>
  )
}
